import { useEffect, useState } from 'react'

import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { PhotoIcon } from '@gto/ui/icons/outline'

export type SiteLogoProps = {
  logo: string | null
  name: string
  size?: 'small' | 'default'
}

export const SiteLogo = ({ logo, name, size }: SiteLogoProps) => {
  const [error, setError] = useState(false)
  const isMdAndLower = useMediaQuery('down', 'md')
  useEffect(
    function loadSiteLogoImage() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- imperative image preload, not derived state: constructs an Image() and subscribes to onerror to fall back to the placeholder icon when the logo URL 404s. There is no React event for "a preloaded Image failed to decode"; react.md:17 blesses effects for this imperative-DOM/subscription shape. The `logo === null` case needs no setState — the render already branches on `logo === null`.
      if (logo === null) {
        return
      }
      const img = new Image()

      img.src = logo
      img.onerror = () => {
        setError(true)
      }
    },
    [logo],
  )

  const isSmall = size === 'small'
  // A null logo shows the placeholder just like a failed load — derive both from
  // one flag so the placeholder's responsive width classes apply in either case.
  // (Previously the null case set `error` in an effect, coupling the two.)
  const showPlaceholder = error || logo === null

  return (
    <div
      className={cn(
        'flex w-[180px] max-w-[180px] items-center justify-center rounded-sm bg-paper-elevation1 p-4 text-primary-main',
        'md:max-w-[100px] md:px-2 md:py-1',
        'sm:max-w-[95px]',
        isSmall && 'max-w-[100px] px-2 py-1',
        isSmall && showPlaceholder && 'w-auto',
        showPlaceholder && 'md:w-auto sm:w-auto',
      )}
    >
      {showPlaceholder ? (
        <PhotoIcon
          color="inherit"
          height={isMdAndLower ? 30 : 40}
        />
      ) : (
        <img
          alt={name}
          loading="lazy"
          src={logo}
          style={{
            width: '100%',
            display: 'block',
            height: 'auto',
            minHeight: '30px',
          }}
        />
      )}
    </div>
  )
}
